import { useEffect, useState } from 'react';
import moment from 'moment';
import { SEARCH_QUERY_NAMES } from '../../constants';
import type { SearchOptionsFragment } from '../../types/millSheetSearch';

/**
 * フリーワード検索のクエリ文字列を変更したときにオプション検索の状態を再構築する。
 * クエリ文字列をフリーワード検索用の文字列とオプション検索用の文字列に分け、
 * オプション検索用の文字列だった場合はそれを分析してオプション検索の状態を返す
 */
const useUpdateOptionStateWithQuery = (query: string): SearchOptionsFragment => {
  const [searchOptionsFragment, setSearchOptionsFragment] = useState<SearchOptionsFragment>({});

  // useEffect(() => {
  //   console.log(searchOptionsFragment);
  // }, [searchOptionsFragment]);

  useEffect(() => {
    // サンプルデータ
    // const query: string = 'メーカー名:日本製鉄 AND 品種:棒線 AND 商社店部課:12E AND 契約番号:9206E21O912907 AND 規格:SWRCH12A AND 寸法:15 AND 溶解番号:M27432 AND 需要家コード1:C3825 AND 需要家コード2:A2012 AND 需要家コード3:O3051 AND 証明書番号:W19180 AND 証明書発行年月日:[2018-01-01 TO 2023-01-13] AND 受渡場所コード:23J25 AND 君津';
    // const query: string = 'メーカー名:日本製鉄 AND 証明書発行年月日:[2020-06-02 TO *] AND 君津 AND SAN:YU';
    // const query: string = 'メーカー名:日本製鉄 AND 証明書発行年月日:[2020-06-02 TO *]';
    // const query: string = '君津 AND SAN-YU';

    // 現在のクエリの内容を調査してオプション検索項目を抽出する。
    // オプション項目かどうかは基本的にはコロンの有無で判断できるが、
    // コロンがあってもオプション検索項目名でない可能性もあるのでチェックしてから処理する
    const querySet = query.split(' AND ');
    // console.log(querySet);
    const fragment: SearchOptionsFragment = querySet.reduce((acc, cur) => {
      const assumedOptionName: string = cur.split(':')[0];
      const isOptionyName: boolean = Object.values(SEARCH_QUERY_NAMES).find((name) => name === assumedOptionName) !== undefined;
      if (cur.indexOf(':') > -1 && isOptionyName) {
        // オプション検索項目名だったときは個別に判定する
        const optionName: string = cur.split(':')[0];
        const optionValue: string = cur.split(':')[1];
        const optionFieldKeyValue: string[] = Object.entries(SEARCH_QUERY_NAMES).find((entry) => entry[1] === optionName);
        const optionFieldName: string = optionFieldKeyValue[0];

        // 証明書発行年月日の場合はstartDateとendDateに分けて中身を調べる。
        // 寸法_径の場合は範囲指定か値指定かを判定し、feInputMaterialSizeDStartとfeInputMaterialSizeDEndに分けて入れる。
        // 寸法_厚さの場合は範囲指定か値指定かを判定し、feInputMaterialSizeDStartとfeInputMaterialSizeDEndに分けて入れる。
        // 寸法_長さの場合は範囲指定か値指定かを判定し、feInputMaterialSizeDStartとfeInputMaterialSizeDEndに分けて入れる。
        // 証明書発行年月日でも寸法_径でも寸法_厚さでも寸法_長さでもなければそのままセット
        if (optionName === '証明書発行年月日') {
          // YYYY-MM-DDの形なら状態に保存する
          const tempStartDate: string = optionValue.split(' TO ')[0].split('[')[1] ?? '';
          const tempEndDate: string = optionValue.split(' TO ')[1]?.split(']')[0] ?? '';
          const isValidStartDate: boolean = moment(tempStartDate, 'YYYY-MM-DD', true).isValid();
          const isValidEndDate: boolean = moment(tempEndDate, 'YYYY-MM-DD', true).isValid();
          const newFragment: SearchOptionsFragment = Object.assign({}, acc, {
            feInputCertIssueStartDate: isValidStartDate ? tempStartDate : '',
            feInputCertIssueEndDate: isValidEndDate ? tempEndDate : '',
          });
          // console.log(newFragment);
          return newFragment;
        } else if (optionName === '寸法_径') {
          // 範囲選択の場合、optionValueに「 TO 」が含まれている。
          // 範囲選択だったら「 TO 」の前の値をStartに、後ろの値をEndに設定し、
          // 範囲選択でなかったらoptionValueをStartに設定する（Endは空）
          const isRangeSize: boolean = optionValue.indexOf(' TO ') > -1;
          const tempStartSize: string = optionValue.split(' TO ')[0].split('[')[1] ?? '';
          const tempEndSize: string = optionValue.split(' TO ')[1]?.split(']')[0] ?? '';
          const newFragment: SearchOptionsFragment = Object.assign({}, acc, {
            feInputMaterialSizeDStart: isRangeSize ? tempStartSize : optionValue,
            feInputMaterialSizeDEnd: isRangeSize ? tempEndSize : '',
          });
          // console.log(newFragment);
          return newFragment;
        } else if (optionName === '寸法_厚さ') {
          // 範囲選択の場合、optionValueに「 TO 」が含まれている。
          // 範囲選択だったら「 TO 」の前の値をStartに、後ろの値をEndに設定し、
          // 範囲選択でなかったらoptionValueをStartに設定する（Endは空）
          const isRangeSize: boolean = optionValue.indexOf(' TO ') > -1;
          const tempStartSize: string = optionValue.split(' TO ')[0].split('[')[1] ?? '';
          const tempEndSize: string = optionValue.split(' TO ')[1]?.split(']')[0] ?? '';
          const newFragment: SearchOptionsFragment = Object.assign({}, acc, {
            feInputMaterialSizeTStart: isRangeSize ? tempStartSize : optionValue,
            feInputMaterialSizeTEnd: isRangeSize ? tempEndSize : '',
          });
          // console.log(newFragment);
          return newFragment;
        } else if (optionName === '寸法_長さ') {
          // 範囲選択の場合、optionValueに「 TO 」が含まれている。
          // 範囲選択だったら「 TO 」の前の値をStartに、後ろの値をEndに設定し、
          // 範囲選択でなかったらoptionValueをStartに設定する（Endは空）
          const isRangeSize: boolean = optionValue.indexOf(' TO ') > -1;
          const tempStartSize: string = optionValue.split(' TO ')[0].split('[')[1] ?? '';
          const tempEndSize: string = optionValue.split(' TO ')[1]?.split(']')[0] ?? '';
          const newFragment: SearchOptionsFragment = Object.assign({}, acc, {
            feInputMaterialSizeLStart: isRangeSize ? tempStartSize : optionValue,
            feInputMaterialSizeLEnd: isRangeSize ? tempEndSize : '',
          });
          // console.log(newFragment);
          return newFragment;
        } else {
          const newFragment: SearchOptionsFragment = Object.assign({}, acc, { [optionFieldName]: optionValue });
          // console.log(newFragment);
          return newFragment;
        }
      } else {
        // オプション検索項目名でなければ何もしない
        return acc;
      }
    }, {} as SearchOptionsFragment);
    // console.log('fragment', fragment);
    setSearchOptionsFragment(fragment);
  }, [query]);

  return searchOptionsFragment;
};

export default useUpdateOptionStateWithQuery;
